type StakeTable = {
  sb: number;
};

type StakeGroup<T extends StakeTable> = readonly [label: string, tables: readonly T[]];

const stakeValue = <T extends StakeTable>([, tables]: StakeGroup<T>) => {
  const value = Number(tables[0]?.sb);
  return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
};

const blindValue = (label: string) => {
  const raw = label.split('/')[1]?.trim().replace(/^\$/, '') ?? '';
  const multiplier = /k$/i.test(raw) ? 1_000 : 1;
  const value = Number(raw.replace(/k$/i, ''));
  return Number.isFinite(value) ? value * multiplier : Number.POSITIVE_INFINITY;
};

export const sortCashStakeGroups = <T extends StakeTable>(groups: readonly StakeGroup<T>[]) =>
  [...groups].sort((left, right) => {
    const smallBlindOrder = stakeValue(left) - stakeValue(right);
    if (smallBlindOrder !== 0) return smallBlindOrder;

    const bigBlindOrder = blindValue(left[0]) - blindValue(right[0]);
    if (bigBlindOrder !== 0) return bigBlindOrder;

    return left[0].localeCompare(right[0]);
  });

/**
 * WITHIN A STAKE (EN-11152, 2026-10-12). Tables a player can sit at right now
 * lead, then tables they can only queue for, then empty tables — joining a
 * live game beats opening a new one. Inside each group, tables follow the
 * caller's format order (cash: Hold'em, PLO, Hi-Lo), then table id, so a seat
 * count ticking up or down never moves a table unless it changes group.
 *
 * Supersedes EN-10883's seated-count order. One rule for every lobby tab: the
 * Sit & Go tab (EN-11139) passes its own format order.
 */
type JoinableTable = {
  tableId: number;
  seated: number;
  capacity: number;
  waitlistCount?: number;
};

const JOINABILITY_ORDER = ['open', 'queue', 'empty'] as const;
export type Joinability = (typeof JOINABILITY_ORDER)[number];

const seatCount = (value: number) => (Number.isFinite(value) ? Math.max(0, value) : 0);

// A missing capacity cannot prove a table full, so a seated one stays open.
const isFull = (players: number, capacity: number) =>
  Number.isFinite(capacity) && capacity > 0 && players >= capacity;

// The queue group matches CashGameTile's Join Waitlist condition: every full
// table offers the waitlist, and so does one with open seats but a line ahead.
const joinabilityOf = ({ seated, capacity, waitlistCount }: JoinableTable): Joinability => {
  const players = seatCount(seated);
  if (isFull(players, capacity) || seatCount(waitlistCount ?? 0) > 0) return 'queue';
  return players === 0 ? 'empty' : 'open';
};

const tableIdValue = (tableId: number) =>
  Number.isFinite(tableId) ? tableId : Number.POSITIVE_INFINITY;

// `groupOrder` lets a tab order the three groups its own way (Sit & Go:
// open, empty, then full — EN-11139 AC 3). Omitted, it is the cash order.
export const sortTablesByJoinability = <T extends JoinableTable, F>(
  tables: readonly T[],
  formatOf: (table: T) => F,
  formatOrder: readonly F[],
  groupOrder: readonly Joinability[] = JOINABILITY_ORDER
) => {
  // An unlisted format trails the listed ones within its group.
  const formatRank = (table: T) => {
    const rank = formatOrder.indexOf(formatOf(table));
    return rank === -1 ? formatOrder.length : rank;
  };

  return [...tables].sort((left, right) => {
    const groupDiff =
      groupOrder.indexOf(joinabilityOf(left)) - groupOrder.indexOf(joinabilityOf(right));
    if (groupDiff !== 0) return groupDiff;

    const formatOrderDiff = formatRank(left) - formatRank(right);
    if (formatOrderDiff !== 0) return formatOrderDiff;

    return tableIdValue(left.tableId) - tableIdValue(right.tableId);
  });
};

const CASH_VARIANT_ORDER = ['NLH', 'PLO', 'PLO-HiLo'] as const;

type CashTable = JoinableTable & {
  variant: string;
};

export const sortCashTables = <T extends CashTable>(tables: readonly T[]) =>
  sortTablesByJoinability<T, string>(tables, (table) => table.variant, CASH_VARIANT_ORDER);
